import BlogInertiaController from '@/actions/App/Http/Controllers/BlogInertiaController';
import { BlogDetailContent } from '@/components/blog/blog-detail-content';
import { BlogPostCard } from '@/components/blog/blog-post-card';
import { Typo } from '@/components/ui/typography';
import { WebBreadcrumb } from '@/components/web/layouts/web-breadcrumb';
import WebLayout from '@/components/web/layouts/web-layout';
import { getBlogImageUrl } from '@/lib/blog';
import { Post } from '@/types/model';
import { Head, Link } from '@inertiajs/react';
import { ChevronRight } from 'lucide-react';
import { useMemo } from 'react';

interface BlogShowPageProps {
  pageTitle: string;
  seoDescription: string;
  ogImage: string | null;
  post: Post;
  relatedPosts: Post[];
}

export default function BlogShow({ pageTitle, seoDescription, ogImage, post, relatedPosts }: BlogShowPageProps) {
  const canonicalUrl = BlogInertiaController.show(post.slug).url;
  const ogImageUrl = ogImage || getBlogImageUrl(post.featured_image, 'blog');
  const logoUrl = typeof window !== 'undefined' ? `${window.location.origin}/logo.png` : '/logo.png';

  const breadcrumbItems = useMemo(() => {
    const items = [{ title: 'Tin tức', href: BlogInertiaController.index().url }];
    if (post.category) {
      items.push({ title: post.category.name, href: BlogInertiaController.index({ query: { category: post.category.slug } }).url });
    }
    items.push({ title: post.title, href: BlogInertiaController.show(post.slug).url });
    return items;
  }, [post]);

  return (
    <WebLayout>
      {/* SEO Meta Tags */}
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={seoDescription} />

        {/* Open Graph Tags */}
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:site_name" content="EventLab" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={ogImageUrl} />

        {/* Article Specific Tags */}
        <meta property="article:published_time" content={post.published_at} />
        <meta property="article:section" content={post.category?.name} />
        {post.tags && post.tags.map((tag) => <meta key={tag.id} property="article:tag" content={tag.name} />)}

        {/* Canonical URL */}
        <link rel="canonical" href={canonicalUrl} />

        {/* Structured Data for BlogPosting */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: post.title,
            description: seoDescription,
            image: ogImageUrl,
            datePublished: post.published_at,
            dateModified: post.published_at,
            author: {
              '@type': 'Organization',
              name: 'EventLab',
            },
            publisher: {
              '@type': 'Organization',
              name: 'EventLab',
              logo: {
                '@type': 'ImageObject',
                url: logoUrl,
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': canonicalUrl,
            },
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbItems.map((item, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: item.title,
              item: typeof window !== 'undefined' ? `${window.location.origin}${item.href}` : item.href,
            })),
          })}
        </script>
      </Head>
      {post.featured_image && (
        <img
          src={getBlogImageUrl(post.featured_image, 'blog')}
          alt={post.title}
          className="mx-auto aspect-[1200/628] h-full max-h-[500px] w-full rounded-b-2xl object-cover object-center shadow-xl"
        />
      )}
      <div className="continer mx-auto mt-8 max-w-4xl">
        <WebBreadcrumb className="mb-6" items={breadcrumbItems} />
      </div>
      <BlogDetailContent post={post} />
      <div className="container mx-auto max-w-4xl">
        {/* Blog Detail Content */}

        {/* Related Posts Section */}
        {relatedPosts.length > 0 && (
          <section className="container mx-auto mt-16 max-w-4xl">
            <div className="mb-8 flex items-center justify-between gap-2">
              <Typo variant="h2">Bài viết liên quan</Typo>
              <Link href={BlogInertiaController.index().url} className="text-muted-foreground hover:text-primary flex items-center text-sm">
                <span className="text-sm">Tất cả</span>
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {relatedPosts.map((relatedPost) => (
                <BlogPostCard key={relatedPost.id} post={relatedPost} />
              ))}
            </div>
            {/* Back to Blog Link */}
            {/* <div className="mt-12 pb-8 text-center">
              <Button variant="outline" asChild>
                <Link href={BlogInertiaController.index().url}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Quay lại danh sách bài viết
                </Link>
              </Button>
            </div> */}
          </section>
        )}
      </div>
    </WebLayout>
  );
}
